/** SWIFT CSCF 2025: 60% Mandatory + 40% Advisory (controls), 50% SA + 50% Controls (total) */
export type SwiftCscfControlStatus = 'IMPLEMENTED' | 'PARTIAL' | 'NOT_IMPLEMENTED' | 'N_A';
const STATUS_TO_PERCENT: Record<SwiftCscfControlStatus, number | null> = {
  IMPLEMENTED: 100,
  PARTIAL: 50,
  NOT_IMPLEMENTED: 0,
  N_A: null,
};

function normalizeStatus(s: string | undefined | null): SwiftCscfControlStatus | null {
  if (!s || typeof s !== 'string') return null;
  const u = s.toUpperCase().replace(/\s+/g, '_');
  if (['IMPLEMENTED', 'PARTIAL', 'NOT_IMPLEMENTED', 'N_A', 'NA'].includes(u)) {
    return (u === 'NA' ? 'N_A' : u) as SwiftCscfControlStatus;
  }
  return null;
}

const MANDATORY_CODES = new Set(['SWIFT_M_01', 'SWIFT_M_02', 'SWIFT_M_03', 'SWIFT_M_04', 'SWIFT_M_05', 'SWIFT_M_06', 'SWIFT_M_07']);

export function computeSwiftCscfControlsScore(controlsAnswers: Record<string, string>) {
  const breakdown = { implemented: 0, partial: 0, notImplemented: 0, na: 0, totalNonNA: 0 };
  let mandatorySum = 0;
  let mandatoryCount = 0;
  let advisorySum = 0;
  let advisoryCount = 0;

  for (const [code, raw] of Object.entries(controlsAnswers)) {
    const status = normalizeStatus(raw);
    if (!status) continue;
    if (status === 'N_A') {
      breakdown.na++;
      continue;
    }
    breakdown.totalNonNA++;
    const pct = STATUS_TO_PERCENT[status];
    if (pct !== null) {
      if (MANDATORY_CODES.has(code)) {
        mandatorySum += pct;
        mandatoryCount++;
      } else {
        advisorySum += pct;
        advisoryCount++;
      }
    }
    if (status === 'IMPLEMENTED') breakdown.implemented++;
    else if (status === 'PARTIAL') breakdown.partial++;
    else breakdown.notImplemented++;
  }

  const mandatoryScore = mandatoryCount > 0 ? Math.round((mandatorySum / (mandatoryCount * 100)) * 100) : 0;
  const advisoryScore = advisoryCount > 0 ? Math.round((advisorySum / (advisoryCount * 100)) * 100) : 0;
  const score = mandatoryCount + advisoryCount > 0 ? Math.round(0.6 * mandatoryScore + 0.4 * advisoryScore) : 0;

  return { score, mandatoryScore, advisoryScore, breakdown };
}

export function computeSwiftCscfScore(params: { selfAssessmentScore?: number; controlsScore?: number }): number {
  const sa = typeof params.selfAssessmentScore === 'number' && Number.isFinite(params.selfAssessmentScore)
    ? Math.max(0, Math.min(100, params.selfAssessmentScore))
    : 0;
  const ctrl = typeof params.controlsScore === 'number' && Number.isFinite(params.controlsScore)
    ? Math.max(0, Math.min(100, params.controlsScore))
    : 0;
  if (sa === 0 && ctrl === 0) return 0;
  return Math.round(0.5 * sa + 0.5 * ctrl);
}
